import * as React from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Smartphone, Share2, MoreHorizontal, Check, Monitor } from "lucide-react";
import { cn } from "@/lib/utils";

interface OnboardingSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ONBOARDING_KEY = "onboardingDone";

export function getOnboardingDone(): boolean {
  if (typeof window === "undefined") return false;
  return localStorage.getItem(ONBOARDING_KEY) === "true";
}

export function setOnboardingDone(): void {
  if (typeof window !== "undefined") {
    localStorage.setItem(ONBOARDING_KEY, "true");
  }
}

function Step({
  num,
  title,
  children,
  done,
}: {
  num: number;
  title: string;
  children: React.ReactNode;
  done?: boolean;
}) {
  return (
    <div className="flex gap-4">
      <div
        className={cn(
          "flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-[10px] font-mono",
          done
            ? "border-primary/40 bg-primary/10 text-primary"
            : "border-border/40 bg-secondary/40 text-muted-foreground",
        )}
      >
        {done ? <Check className="h-3 w-3" /> : num}
      </div>
      <div className="flex-1">
        <p className="text-xs font-medium text-foreground">{title}</p>
        <div className="mt-1 text-[11px] text-muted-foreground">{children}</div>
      </div>
    </div>
  );
}

export function OnboardingSheet({ open, onOpenChange }: OnboardingSheetProps) {
  const handleDismiss = () => {
    setOnboardingDone();
    onOpenChange(false);
  };

  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  const isIOS = /iPhone|iPad|iPod/.test(ua);
  const isAndroid = /Android/.test(ua);
  // Anything that is not a recognised phone/tablet gets the desktop welcome —
  // install-to-home-screen steps are meaningless there.
  const isMobile = isIOS || isAndroid || /Mobile|Silk|Kindle/.test(ua);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="flex max-h-[85vh] flex-col p-0 sm:max-h-[80vh]"
      >
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/60 to-transparent"
          aria-hidden
        />
        <SheetHeader className="border-b border-border/40 px-5 pb-4 pt-5">
          <div className="flex items-center gap-2">
            <span className="inline-flex h-6 w-6 items-center justify-center rounded-md border border-primary/40 bg-primary/10 text-primary shadow-[0_0_12px_hsl(var(--primary)/0.25)]">
              {isMobile ? (
                <Smartphone className="h-3 w-3" />
              ) : (
                <Monitor className="h-3 w-3" />
              )}
            </span>
            <SheetTitle className="gradient-text text-base">
              {isMobile ? "Add to Home Screen" : "Welcome to CodeZero"}
            </SheetTitle>
          </div>
          <SheetDescription className="text-muted-foreground">
            {isMobile
              ? "Install CodeZero as an app for the best experience."
              : "Drive Claude Code sessions on your machines from one console."}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 space-y-6 overflow-y-auto px-5 py-5">
          {!isMobile ? (
            <div className="space-y-4">
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground">
                Getting started
              </p>
              <div className="space-y-4">
                <Step num={1} title="Start a session">
                  Pick a project in the sidebar and hit{" "}
                  <span className="text-primary">New Session</span> to open a Claude
                  Code session on this machine.
                </Step>
                <Step num={2} title="Connect another machine">
                  Open <span className="text-primary">Machines</span> in the header to
                  link a remote machine through the hub, then start sessions on it
                  from here.
                </Step>
                <Step num={3} title="Move fast">
                  Press <span className="text-primary">Cmd/Ctrl + K</span> for the
                  command palette — sessions, navigation, and actions in one place.
                </Step>
              </div>
            </div>
          ) : isIOS ? (
            <div className="space-y-4">
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground">
                iOS (Safari)
              </p>
              <div className="space-y-4">
                <Step num={1} title="Tap the share button">
                  In Safari, tap the <Share2 className="inline h-3 w-3" /> button in the toolbar.
                </Step>
                <Step num={2} title="Scroll down and tap Add to Home Screen">
                  Scroll down in the share sheet and tap{" "}
                  <span className="text-primary">Add to Home Screen</span>.
                </Step>
                <Step num={3} title="Tap Add">
                  Confirm by tapping <span className="text-primary">Add</span> in the top right.
                </Step>
              </div>
            </div>
          ) : isAndroid ? (
            <div className="space-y-4">
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground">
                Android (Chrome)
              </p>
              <div className="space-y-4">
                <Step num={1} title="Tap the menu">
                  Tap the <MoreHorizontal className="inline h-3 w-3" /> (three dots) menu in Chrome.
                </Step>
                <Step num={2} title="Tap Install App">
                  Select <span className="text-primary">Install App</span> or{" "}
                  <span className="text-primary">Add to Home Screen</span>.
                </Step>
                <Step num={3} title="Confirm">
                  Tap <span className="text-primary">Install</span> to confirm.
                </Step>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground">
                Instructions
              </p>
              <div className="space-y-4">
                <Step num={1} title="Open browser menu">
                  Tap the menu button in your browser (usually three dots or "Share").
                </Step>
                <Step num={2} title="Find Install option">
                  Look for <span className="text-primary">Add to Home Screen</span> or{" "}
                  <span className="text-primary">Install App</span>.
                </Step>
                <Step num={3} title="Confirm">
                  Tap <span className="text-primary">Install</span> or{" "}
                  <span className="text-primary">Add</span> to add CodeZero to your home screen.
                </Step>
              </div>
            </div>
          )}

          <div className="rounded-lg border border-primary/20 bg-primary/5 p-4">
            {isMobile ? (
              <p className="text-[11px] text-muted-foreground">
                <span className="text-primary font-medium">Why install?</span> Installed apps
                load faster, work offline, and feel like native apps. No address bar, no tabs —
                just CodeZero.
              </p>
            ) : (
              <p className="text-[11px] text-muted-foreground">
                <span className="text-primary font-medium">On the go?</span> Open this same
                URL on your phone and add it to the home screen to follow and steer running
                sessions from anywhere.
              </p>
            )}
          </div>
        </div>

        <div className="border-t border-border/40 px-5 py-4">
          <Button onClick={handleDismiss} className="w-full" variant="hero">
            Got it
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default OnboardingSheet;